import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  TaapAccountRow,
  TaapBreakdown,
  TaapCampaignRow,
  TaapPlatformRow,
} from '../../types/taap';
import { formatMoney, formatNumber, formatPercent, formatRatio } from '../../utils/taapFormat';

/** Meta の effective_status と Google の status のうち、いま配信していない状態。 */
const STOPPED_STATUSES = new Set(['PAUSED', 'CAMPAIGN_PAUSED', 'ARCHIVED', 'DELETED', 'REMOVED']);

interface Props {
  breakdown: TaapBreakdown | null;
  loading: boolean;
  currency: string;
  /** 売上が広告に紐づかない商材では ROAS 列を出さない。 */
  showRoas: boolean;
  /**
   * アカウント階層を挟むか。
   *
   * 商品に複数アカウントがあり、アカウントを選んでいないときだけ挟む。
   * 1アカウントだけなら見出しのセレクタで分かるので、表の中で同じ名前を
   * もう一段挟むと一階層ぶん無駄にインデントが深くなるだけになる。
   */
  showAccountLevel: boolean;
}

/**
 * 媒体 → アカウント → キャンペーン → 広告グループ。
 *
 * どの階層も同じ ad 粒度の実績を畳んだものなので、展開しても合計が
 * ずれない。行クリックで一段ずつ開く。
 */
const TaapBreakdownTable: React.FC<Props> = ({ breakdown, loading, currency, showRoas, showAccountLevel }) => {
  const [openPlatforms, setOpenPlatforms] = useState<Set<string>>(new Set());
  const [openAccounts, setOpenAccounts] = useState<Set<string>>(new Set());
  const [openCampaigns, setOpenCampaigns] = useState<Set<string>>(new Set());

  const toggle = (setter: React.Dispatch<React.SetStateAction<Set<string>>>) => (key: string) => {
    setter((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const togglePlatform = toggle(setOpenPlatforms);
  const toggleAccount = toggle(setOpenAccounts);
  const toggleCampaign = toggle(setOpenCampaigns);

  if (loading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
        読み込み中…
      </div>
    );
  }

  if (!breakdown || breakdown.platforms.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
        表示できる配信実績がまだありません。
      </div>
    );
  }

  const columnCount = showRoas ? 7 : 6;
  const campaignPad = showAccountLevel ? 'pl-16' : 'pl-10';
  const adGroupPad = showAccountLevel ? 'pl-[5.5rem]' : 'pl-16';

  const renderCampaign = (campaign: TaapCampaignRow, campaignKey: string) => {
    const campaignOpen = openCampaigns.has(campaignKey);

    return (
      <React.Fragment key={campaignKey}>
        <tr
          className="cursor-pointer border-b border-gray-100 bg-gray-50/40 hover:bg-gray-100"
          onClick={() => toggleCampaign(campaignKey)}
        >
          <td className={`py-2.5 pr-4 ${campaignPad}`}>
            <div className="flex items-center gap-2">
              {campaignOpen ? (
                <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
              )}
              <span className="text-gray-800">{campaign.campaign_name ?? '(名称なし)'}</span>
              {campaign.campaign_status && STOPPED_STATUSES.has(campaign.campaign_status) && (
                <span className="rounded bg-gray-200 px-1.5 py-0.5 text-xs text-gray-600">停止中</span>
              )}
              <span className="text-xs text-gray-500">{campaign.ad_group_count} 広告グループ</span>
            </div>
          </td>
          <td className="px-4 py-2.5 text-right tabular-nums">{formatMoney(campaign.cost, currency)}</td>
          <td className="px-4 py-2.5 text-right tabular-nums">{formatNumber(campaign.conversions)}</td>
          <td className="px-4 py-2.5 text-right tabular-nums">
            {formatRatio(campaign.cpa, campaign.conversions, (v) => formatMoney(v, currency))}
          </td>
          <td className="px-4 py-2.5 text-right tabular-nums">{formatPercent(campaign.ctr, 2)}</td>
          <td className="px-4 py-2.5 text-right tabular-nums">{formatPercent(campaign.cvr, 2)}</td>
          {showRoas && (
            <td className="px-4 py-2.5 text-right tabular-nums">
              {formatRatio(campaign.roas, campaign.revenue, (v) => formatPercent(v, 0))}
            </td>
          )}
        </tr>

        {campaignOpen &&
          campaign.ad_groups.map((adGroup) => (
            <tr
              key={`${campaignKey}:${adGroup.platform_adset_id}`}
              className="border-b border-gray-100 text-gray-600"
            >
              <td className={`py-2 pr-4 text-[13px] ${adGroupPad}`}>{adGroup.adset_name ?? '(名称なし)'}</td>
              <td className="px-4 py-2 text-right text-[13px] tabular-nums">
                {formatMoney(adGroup.cost, currency)}
              </td>
              <td className="px-4 py-2 text-right text-[13px] tabular-nums">
                {formatNumber(adGroup.conversions)}
              </td>
              <td className="px-4 py-2 text-right text-[13px] tabular-nums">
                {formatRatio(adGroup.cpa, adGroup.conversions, (v) => formatMoney(v, currency))}
              </td>
              <td className="px-4 py-2 text-right text-[13px] tabular-nums">{formatPercent(adGroup.ctr, 2)}</td>
              <td className="px-4 py-2 text-right text-[13px] tabular-nums">{formatPercent(adGroup.cvr, 2)}</td>
              {showRoas && (
                <td className="px-4 py-2 text-right text-[13px] tabular-nums">
                  {formatRatio(adGroup.roas, adGroup.revenue, (v) => formatPercent(v, 0))}
                </td>
              )}
            </tr>
          ))}
      </React.Fragment>
    );
  };

  const renderAccount = (account: TaapAccountRow, platformKey: string) => {
    const accountKey = `${platformKey}:${account.ad_account_id}`;
    const accountOpen = openAccounts.has(accountKey);

    return (
      <React.Fragment key={accountKey}>
        <tr
          className="cursor-pointer border-b border-gray-100 bg-gray-50/60 hover:bg-gray-100"
          onClick={() => toggleAccount(accountKey)}
        >
          <td className="py-2.5 pl-10 pr-4">
            <div className="flex items-center gap-2">
              {accountOpen ? (
                <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
              )}
              <span className="font-medium text-gray-900">
                {account.account_name ?? `アカウント #${account.ad_account_id}`}
              </span>
              <span className="text-xs text-gray-500">
                {account.campaign_count} キャンペーン · 費用シェア {formatPercent(account.cost_share, 0)}
              </span>
            </div>
          </td>
          <td className="px-4 py-2.5 text-right font-medium tabular-nums">
            {formatMoney(account.cost, currency)}
          </td>
          <td className="px-4 py-2.5 text-right tabular-nums">{formatNumber(account.conversions)}</td>
          <td className="px-4 py-2.5 text-right tabular-nums">
            {formatRatio(account.cpa, account.conversions, (v) => formatMoney(v, currency))}
          </td>
          <td className="px-4 py-2.5 text-right tabular-nums">{formatPercent(account.ctr, 2)}</td>
          <td className="px-4 py-2.5 text-right tabular-nums">{formatPercent(account.cvr, 2)}</td>
          {showRoas && (
            <td className="px-4 py-2.5 text-right tabular-nums">
              {formatRatio(account.roas, account.revenue, (v) => formatPercent(v, 0))}
            </td>
          )}
        </tr>

        {accountOpen &&
          account.campaigns.map((campaign) =>
            renderCampaign(campaign, `${accountKey}:${campaign.campaign_id}`),
          )}
      </React.Fragment>
    );
  };

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-xs text-gray-500">
              <th className="px-4 py-2.5 text-left font-medium">
                {showAccountLevel
                  ? '媒体 / アカウント / キャンペーン / 広告グループ'
                  : '媒体 / キャンペーン / 広告グループ'}
              </th>
              <th className="px-4 py-2.5 text-right font-medium">費用</th>
              <th className="px-4 py-2.5 text-right font-medium">CV</th>
              <th className="px-4 py-2.5 text-right font-medium">CPA</th>
              <th className="px-4 py-2.5 text-right font-medium">CTR</th>
              <th className="px-4 py-2.5 text-right font-medium">CVR</th>
              {showRoas && <th className="px-4 py-2.5 text-right font-medium">ROAS</th>}
            </tr>
          </thead>
          <tbody>
            {breakdown.platforms.map((platform: TaapPlatformRow) => {
              const platformOpen = openPlatforms.has(platform.platform);

              return (
                <React.Fragment key={platform.platform}>
                  <tr
                    className="cursor-pointer border-b border-gray-100 hover:bg-gray-50"
                    onClick={() => togglePlatform(platform.platform)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {platformOpen ? (
                          <ChevronDown className="h-4 w-4 text-gray-400" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-400" />
                        )}
                        <span className="font-semibold text-gray-900">{platform.label}</span>
                        <span className="text-xs text-gray-500">
                          {showAccountLevel && platform.account_count > 1
                            ? `${platform.account_count} アカウント · ${platform.campaign_count} キャンペーン`
                            : `${platform.campaign_count} キャンペーン`}
                          {' · 費用シェア '}
                          {formatPercent(platform.cost_share, 0)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-medium tabular-nums">
                      {formatMoney(platform.cost, currency)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatNumber(platform.conversions)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {formatRatio(platform.cpa, platform.conversions, (v) => formatMoney(v, currency))}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatPercent(platform.ctr, 2)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatPercent(platform.cvr, 2)}</td>
                    {showRoas && (
                      <td className="px-4 py-3 text-right tabular-nums">
                        {formatRatio(platform.roas, platform.revenue, (v) => formatPercent(v, 0))}
                      </td>
                    )}
                  </tr>

                  {platformOpen &&
                    (showAccountLevel
                      ? platform.accounts.map((account) => renderAccount(account, platform.platform))
                      : platform.accounts.flatMap((account) =>
                          account.campaigns.map((campaign) =>
                            renderCampaign(
                              campaign,
                              `${platform.platform}:${account.ad_account_id}:${campaign.campaign_id}`,
                            ),
                          ),
                        ))}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50 text-xs text-gray-500">
              <td colSpan={columnCount} className="px-4 py-2">
                {showAccountLevel
                  ? '行をクリックすると アカウント → キャンペーン → 広告グループ の順に展開します'
                  : '行をクリックすると キャンペーン → 広告グループ の順に展開します'}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default React.memo<Props>(TaapBreakdownTable);
